import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Link, Redirect, useRouteMatch } from 'react-router-dom';
import { Tab, Tabs } from '@material-ui/core';
import classNames from 'classnames';
import copy from 'copy-to-clipboard';
import { format, isValid } from 'date-fns';

import { brandConstants } from 'brand-config';
import { Loading } from 'core';
import { Label } from 'core/components/label';
import { Tag } from 'core/components/tag';
import { CopyButton } from 'core/components/copy-button';
import { useToast } from 'core/components/toast';
import { useViewAsSubject } from 'core/components/view-as';
import useSession from 'core/hooks/use-session';
import { useScroll } from 'core/hooks/use-scroll';
import { useQueryParams } from 'core/hooks/useQueryParams';
import { PolicyStatus } from 'core/helpers/policy-status';
import { useStore } from 'core/store';
import { UmbrellaCancelAtRenewalBanner } from 'common/components/umbrella-cancel-at-renewal-banner';
import { CustomerOverview } from 'customer/customer';
import Documents from '../documents';
import Tickets from '../tickets';
import Claims from '../claims';
import FrontContact from '../front-contact';
import { CustomerNotifications } from '../customer-notifications';
import { HouseholdBilling } from '../household-billing';
import { CustomerSidebar } from '../sidebar';
import { Policy } from '../policy';
import { PolicyHeader } from '../policy/header';
import { PolicySidebar } from '../policy-sidebar';
import { canSeeInteractionsAndClaims } from '../tabs';
import { getPolicyProducer, useAgency } from '../agent-details';
import {
  WorkspaceSections,
  getAffinityChips,
  getBundleLabel,
  getCustomerStatus,
  getCustomerStatusLabel,
  getInitials,
  getOpenPolicies,
  getPolicyAffinity,
  getWorkspaceTabs,
  isAutopayOn
} from './workspace-summary';
import useStyles from './customer-workspace.styles';

const formatCustomerSince = (created) => {
  const date = created ? new Date(created) : null;
  return date && isValid(date) ? `Customer since ${format(date, 'MMM yyyy')}` : null;
};

const AccountId = ({ accountId, className }) => {
  const { notify } = useToast();
  const copyAccountId = () => {
    const copied = copy(accountId);
    if (!copied) {
      notify({ type: 'error', message: `${accountId} couldn't be copied` });
    }
    return copied;
  };
  return (
    <span className={className}>
      {accountId}
      <CopyButton onCopy={copyAccountId} aria-label="Copy account ID" />
    </span>
  );
};

AccountId.propTypes = {
  accountId: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired
};

const WorkspaceHeader = observer(function WorkspaceHeader({ accountId, shownPolicy }) {
  const classes = useStyles();
  const session = useSession();
  const { account } = useStore();
  const policies = account.policies.list;
  const recentPolicy = policies.length ? account.policies.getMostRecentActiveOrFuturePolicy() : null;
  const agencyAffinity = getPolicyAffinity(recentPolicy);
  const agency = useAgency(agencyAffinity);

  // View as borrows the agency of the policy on screen, or of the Agency line's policy on Overview.
  const viewAsPolicy = shownPolicy ?? recentPolicy;
  const viewAsAffinity = getPolicyAffinity(viewAsPolicy);
  const sameAgency = viewAsAffinity === agencyAffinity;
  const viewAsAgency = useAgency(sameAgency ? null : viewAsAffinity);
  useViewAsSubject({
    affinity: viewAsAffinity,
    state: viewAsPolicy?.state,
    agencyName: (sameAgency ? agency : viewAsAgency).agencyName
  });

  // The store keeps the account while a tab switch refetches it, so the name doesn't flash.
  const hasAccount = account.id === accountId && Boolean(account.fname || account.lname);
  const name = hasAccount ? `${account.fname} ${account.lname}` : 'Loading...';

  const producer = getPolicyProducer(recentPolicy);
  let agencyLine = null;
  if (agency.isAgentSold) {
    agencyLine = agency.agencyName ? [`Agency: ${agency.agencyName}`, producer].filter(Boolean).join(' · ') : null;
  } else if (recentPolicy && (!agencyAffinity || agency.affinityData)) {
    agencyLine = `${brandConstants.SHORT_NAME} direct`;
  }

  const openPolicies = getOpenPolicies(policies);
  const status = getCustomerStatus(policies);
  // With one policy, the customer's status is that policy's, which its tab's header already shows.
  const showStatusChip = status && !(shownPolicy && policies.length === 1);
  const bundleLabel = getBundleLabel(openPolicies);
  const affinityChips = session.isInternalAgent
    ? getAffinityChips({
        memberAffinityCode: account.memberAffinityCode,
        policyAffinityCode: getPolicyAffinity(shownPolicy ?? recentPolicy)
      })
    : [];

  return (
    <header className={classes.header}>
      <div className={classes.initials} aria-hidden="true">
        {hasAccount ? getInitials(account) : ''}
      </div>
      <div className={classes.identity}>
        <Label type="title" component="h1" className={classes.name}>
          {name}
        </Label>
        {hasAccount && (
          <div className={classes.meta}>
            {formatCustomerSince(account.created) && <span>{formatCustomerSince(account.created)}</span>}
            {account.mailingAddress?.city && (
              <span>
                {account.mailingAddress.city}, {account.mailingAddress.state}
              </span>
            )}
            {agencyLine && <span>{agencyLine}</span>}
            <AccountId accountId={accountId} className={classes.accountId} />
            {!session.isExternalSales && <FrontContact inline />}
          </div>
        )}
        {hasAccount && (
          <div className={classes.chips}>
            {showStatusChip && (
              <Tag
                className={classNames(classes.statusChip, {
                  [classes.statusChipActive]: status === PolicyStatus.Active
                })}
              >
                {getCustomerStatusLabel(status)}
              </Tag>
            )}
            {bundleLabel && <Tag className={classes.chip}>{bundleLabel}</Tag>}
            {isAutopayOn(openPolicies) && <Tag className={classes.chip}>Autopay on</Tag>}
            {affinityChips.map((chip) => (
              <Tag key={chip} className={classes.affinityChip}>
                {chip}
                <Tag variant="internal">Internal</Tag>
              </Tag>
            ))}
          </div>
        )}
      </div>
    </header>
  );
});

WorkspaceHeader.propTypes = {
  accountId: PropTypes.string.isRequired,
  shownPolicy: PropTypes.object
};

const TwoColumns = ({ main, side }) => {
  const classes = useStyles();
  return (
    <div className={classes.columns}>
      <div className={classes.mainColumn}>{main}</div>
      <div className={classes.sideColumn}>{side}</div>
    </div>
  );
};

TwoColumns.propTypes = {
  main: PropTypes.node.isRequired,
  side: PropTypes.node.isRequired
};

const CustomerSection = observer(function CustomerSection({ section, accountId, history }) {
  const { scrollTo } = useQueryParams();
  useScroll(scrollTo);
  const {
    account: { accountLoading }
  } = useStore();

  return (
    <>
      {!accountLoading && <UmbrellaCancelAtRenewalBanner />}
      {!accountLoading && <CustomerNotifications />}
      {section === WorkspaceSections.OVERVIEW && (
        <TwoColumns main={<CustomerOverview />} side={<CustomerSidebar history={history} accountId={accountId} />} />
      )}
      {section === WorkspaceSections.BILLING && <HouseholdBilling accountId={accountId} />}
      {section === WorkspaceSections.DOCUMENTS && <Documents showPolicyIdColumn />}
      {section === WorkspaceSections.INTERACTIONS && <Tickets accountId={accountId} />}
      {section === WorkspaceSections.CLAIMS && <Claims />}
    </>
  );
});

/**
 * The condensed layout's customer page (prototype option A): one header and tab row on both the
 * customer and the policy routes. Each tab is a route, so a policy opens as a tab of its customer.
 */
const CustomerWorkspace = observer(function CustomerWorkspace({ accountId, policyId, history }) {
  const classes = useStyles();
  const session = useSession();
  const { account } = useStore();
  const sectionMatch = useRouteMatch('/customer/:id/:section');

  const policies = account.policies.list.toJSON();
  const tabs = getWorkspaceTabs({
    accountId,
    policies,
    currentPolicyId: policyId,
    // Same gate as the policy page's Payment & Price tab.
    showBilling: Boolean(session.canViewPolicies),
    showInteractionsAndClaims: canSeeInteractionsAndClaims({ session, policies })
  });
  const requestedSection = sectionMatch?.params.section;
  const isGatedSection = [WorkspaceSections.BILLING, WorkspaceSections.INTERACTIONS, WorkspaceSections.CLAIMS].includes(
    requestedSection
  );
  const section =
    policyId ??
    (Object.values(WorkspaceSections).includes(requestedSection) ? requestedSection : WorkspaceSections.OVERVIEW);
  const sectionAllowed = !isGatedSection || tabs.some((tab) => tab.value === section);

  if (!sectionAllowed && !account.accountLoading) {
    return <Redirect to={`/customer/${accountId}`} />;
  }

  const shownPolicy = policyId ? policies.find((policy) => policy.id === policyId) : null;
  const name = account.id === accountId ? `${account.fname ?? ''} ${account.lname ?? ''}`.trim() : '';

  let panel;
  if (!sectionAllowed) {
    panel = <Loading />;
  } else if (policyId) {
    panel = (
      <>
        <PolicyHeader history={history} />
        <TwoColumns
          main={<Policy history={history} />}
          side={<PolicySidebar history={history} accountId={accountId} policyId={policyId} />}
        />
      </>
    );
  } else {
    panel = <CustomerSection section={section} accountId={accountId} history={history} />;
  }

  return (
    <div className={classes.workspace}>
      <nav aria-label="Breadcrumb" className={classes.breadcrumb}>
        <Link to="/search/customers">Search</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{name || 'Customer'}</span>
      </nav>
      <WorkspaceHeader accountId={accountId} shownPolicy={shownPolicy} />
      <Tabs
        value={tabs.some((tab) => tab.value === section) ? section : false}
        variant="scrollable"
        scrollButtons="off"
        aria-label="Customer"
        className={classes.tabs}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={tab.label}
            title={tab.title}
            component={Link}
            to={tab.to}
            className={classes.tab}
          />
        ))}
      </Tabs>
      <div className={classes.panel}>{panel}</div>
    </div>
  );
});

CustomerWorkspace.propTypes = {
  accountId: PropTypes.string.isRequired,
  policyId: PropTypes.string,
  history: PropTypes.object.isRequired
};

export default CustomerWorkspace;
